import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Chip, FormLabel } from '@mui/joy';

import {
  CHAT_MODEL_MAPPING,
  getModelDisplayNameFromIdList,
} from '@/Pages/Playground/Playground.constants';
import {
  ManifestCommonOpenAIProviderType,
  ManifestOpenAISeed,
} from '@/Pages/Playground/components/manifest/serialized/common/ManifestCommonOpenAI';
import OpenAILogo from '@/icons/OpenAILogo.svg?react';
import { cn } from '@/utils/tailwind';

import { InstructModelBadge } from '../../InstructModelBadge';
import {
  ManifestEditorProps,
  ManifestPreviewProps,
  tableVariantContainerClass,
} from '../../Manifest.utils';
import { ManifestNavigation } from '../../ManifestNavigation';
import { ManifestSelectModel } from '../../ManifestSelectModel';
import { ManifestProviderReplaceWithSwitch } from '../../ManifestSelectProvider';
import { ManifestSlider } from '../../ManifestSlider';
import { ManifestStopEditor } from '../../ManifestStopEditor';
import { ManifestJSAzureOpenAIEditor } from '../common/ManifestCommonAzureOpenAI';
import { DEFAULT_MODEL, MODELS } from './ManifestOpenAI.utils';

export function ManifestOpenAI(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs;

  return (
    <>
      <ManifestNavigation navigation={props.navigation} />

      <div
        className={cn(
          'flex flex-col gap-3',
          props.variant === 'table' && tableVariantContainerClass
        )}
      >
        <ManifestProviderReplaceWithSwitch
          variant={props.variant}
          onChange={props.onChange}
          value={props.value}
          readOnly={props.readOnly}
          isChatModel={false}
          otherModelId={CHAT_MODEL_MAPPING['ChatOpenAI'].modelId}
        />

        <ManifestSelectModel
          variant={props.variant}
          models={MODELS}
          dynamic={{
            id: props.value.id,
            secrets: props.secrets,
          }}
          onChange={(model) => {
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, model },
            });
          }}
          value={kwargs.model ?? DEFAULT_MODEL}
          readOnly={props.readOnly}
        />

        <ManifestSlider
          title="Temperature"
          value={kwargs.temperature}
          min={0}
          max={2}
          step={0.1}
          defaultValue={0.7}
          onChange={(temperature) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, temperature },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Maximum Length"
          value={kwargs.max_tokens == null ? -1 : kwargs.max_tokens}
          min={-1}
          max={2000}
          defaultValue={256}
          step={1}
          onChange={(max_tokens) =>
            props.onChange({
              ...props.value,
              kwargs: {
                ...kwargs,
                max_tokens: max_tokens === -1 ? null : max_tokens,
              },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Top P"
          value={kwargs.top_p}
          min={0}
          max={1}
          step={0.01}
          defaultValue={1}
          onChange={(top_p) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, top_p },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Presence Penalty"
          value={kwargs.presence_penalty}
          min={0}
          max={2}
          step={0.01}
          defaultValue={0}
          onChange={(presence_penalty) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, presence_penalty },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Frequency Penalty"
          value={kwargs.frequency_penalty}
          min={0}
          max={2}
          step={0.01}
          defaultValue={0}
          onChange={(frequency_penalty) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, frequency_penalty },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        {!props.readOnly && <ManifestOpenAISeed {...props} />}

        {props.options && (!props.readOnly || !!props.options.stop) && (
          <div className="flex flex-col gap-2">
            <FormLabel>Stop Sequences</FormLabel>
            <ManifestStopEditor
              value={props.options.stop}
              onChange={(stop) =>
                props.onOptionsChange({
                  ...props.options,
                  stop,
                })
              }
              readOnly={props.readOnly}
            />
          </div>
        )}

        {!props.readOnly && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">Provider Options</div>
            <ManifestCommonOpenAIProviderType {...props} />
          </div>
        )}

        <ManifestJSAzureOpenAIEditor {...props} />
      </div>
    </>
  );
}

export function ManifestOpenAIPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const kwargs = props.value.kwargs;
  const model = kwargs.model ?? DEFAULT_MODEL;

  if (props.variant === 'line') {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="font-semibold">{displayLabel}</span>
        <span>{model}</span>
        <InstructModelBadge />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        'flex w-auto select-none flex-col rounded-md bg-neutral-50 p-4 shadow-sm dark:bg-[rgb(9,9,13)]',
        props.onClick ? 'cursor-pointer' : 'cursor-default'
      )}
    >
      <span className="flex items-center gap-2">
        <OpenAILogo />
        <span className="font-semibold">{displayLabel}</span>
      </span>

      <span className="mt-3 flex items-center gap-1">
        <Chip variant="outlined" size="sm" color="neutral">
          Model: {model}
        </Chip>

        {kwargs.temperature != null && (
          <Chip variant="outlined" size="sm" color="neutral">
            Temperature: {kwargs.temperature}
          </Chip>
        )}

        {kwargs.max_tokens != null && (
          <Chip variant="outlined" size="sm" color="neutral">
            Maximum Length: {kwargs.max_tokens}
          </Chip>
        )}
      </span>
    </button>
  );
}
